/**
 * @file    FieldsManager.tsx
 * @scope   VIEW
 * @summary Add/Remove Fields screen: renders the partition's field cards, the header (search/add)
 *          and wires navigation to the per-field details, filters and advanced-settings sub-views.
 * @consumes useFieldsManager, useFieldsFilter, FieldsManager models
 * @produces `FieldsManager` component + `FieldsManagerTab` descriptor
 * @sideEffects None directly — all state/effects live in the hooks it consumes.
 * @notes Presenter half of the Container/Presenter split; keep it declarative (logic in the hooks).
 */
import React from 'react';
import { Skeleton, SkeletonVariants } from '@cx/ui';
import { Tab } from 'components/TabNavigator/TabNavigator';
import {
  SelectedFieldModel,
  SelectedUserFields,
  FieldsManagerViewOptionsModel,
} from 'models/Enterprise/FieldsManagerModel';
import { PartitionModel } from 'models/Enterprise/DataSourceModel';
import { useFieldsManager } from './useFieldsManager';
import { useFieldsFilter } from './useFieldsFilter';
import FieldsManagerHeader from './FieldsManagerHeader';
import FieldItemCard from './FieldItemCard';
import './FieldsManager.scss';

export const FieldsManagerTab: Tab = {
  name: 'FieldsManager',
  displayName: 'Add/Remove Fields',
};

interface FieldsManagerProps {
  onBack: () => void;
  selectedUserFields?: SelectedUserFields;
  fieldsManagerViewOptions?: FieldsManagerViewOptionsModel;
  onSelectedUserFieldsChange?: (fields: SelectedUserFields) => void;
  onViewOptionsLoaded?: (options: FieldsManagerViewOptionsModel) => void;
  endpoint: string;
  selectedPartition: PartitionModel;
  onAdvancedSettings?: (field: SelectedFieldModel, fieldIndex: number) => void;
  /** Navigate to FiltersSettingsManager — filterId=null means "Add" */
  onEditFilter?: (fieldIndex: number, filterId: string | null) => void;
  /** Navigate to PartitionFiltersManager */
  onManagePartitionFilters?: () => void;
  expandedIndex?: number | null;
  /** When set, the expanded card will start with this tab active */
  defaultActiveTab?: 'details' | 'filters';
}

const renderFieldSkeleton = (key: number) => (
  <div key={key} className="field-item field-item-skeleton">
    <Skeleton variant={SkeletonVariants.rectangular} height="82px" />
  </div>
);

const FieldsManager: React.FC<FieldsManagerProps> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onBack: _onBack,
  fieldsManagerViewOptions: fieldsManagerViewOptionsProp,
  selectedUserFields = { partitionGuid: '', fields: [] },
  onSelectedUserFieldsChange,
  onViewOptionsLoaded,
  endpoint,
  selectedPartition,
  onAdvancedSettings,
  onEditFilter,
  onManagePartitionFilters,
  expandedIndex = null,
  defaultActiveTab,
}) => {
  const {
    isLoading,
    availableFields,
    availableFilterFields,
    getCalculationTypesForField,
    expandedFieldIndices,
    highlightedFieldIndex,
    touchedFieldIndices,
    fieldStates,
    dataSourceDisplayName,
    fieldCountText,
    filterCountText,
    hasInvalidFields,
    hasInvalidPartitionFilters,
    setFieldItemRef,
    handleAddField,
    handleRemoveField,
    handleFieldSelect,
    handleDisplayNameChange,
    handleCalculationTypeChange,
    handlePrecisionChange,
    handleNormalizeChange,
    handleStatTestingChange,
    handleIsL1CategoryChange,
    handleReorderFields,
    handleAccordionToggle,
    collapseAllFields,
    expandAllFields,
    handleFieldItemBlur,
    handleFilterRemove,
  } = useFieldsManager({
    selectedUserFields,
    fieldsManagerViewOptionsProp,
    onSelectedUserFieldsChange,
    onViewOptionsLoaded,
    endpoint,
    selectedPartition,
    expandedIndex,
  });

  const { searchTerm, isSearchMode, visibleFields, handleSearchTermChange } = useFieldsFilter({
    fields: selectedUserFields.fields,
    onSearchTermChange: collapseAllFields,
  });

  const areAllFieldsExpanded =
    selectedUserFields.fields.length > 0 &&
    expandedFieldIndices.size === selectedUserFields.fields.length;

  const handleToggleExpandAll = (isExpanded: boolean) =>
    isExpanded ? expandAllFields() : collapseAllFields();

  return (
    <div className="manage-fields-content">
      <FieldsManagerHeader
        isLoading={isLoading}
        dataSourceDisplayName={dataSourceDisplayName}
        fieldCountText={fieldCountText}
        filterCountText={filterCountText}
        hasInvalidFields={hasInvalidFields}
        hasInvalidPartitionFilters={hasInvalidPartitionFilters}
        isSearchMode={isSearchMode}
        searchTerm={searchTerm}
        areAllFieldsExpanded={areAllFieldsExpanded}
        onAddField={handleAddField}
        onSearchTermChange={handleSearchTermChange}
        onToggleExpandAll={handleToggleExpandAll}
        onManagePartitionFilters={onManagePartitionFilters}
      />

      {/* Fields List */}
      {selectedUserFields.fields.length > 0 ? (
        <div className={`fields-list${isLoading ? ' fields-manager-skeleton-list' : ''}`}>
          {isLoading
            ? selectedUserFields.fields.map((_, i) => renderFieldSkeleton(i))
            : visibleFields.map(({ field, index }) => {
                const fieldState = fieldStates.get(index);
                if (!fieldState) return null;

                return (
                  <FieldItemCard
                    key={index}
                    field={field}
                    index={index}
                    fieldState={fieldState}
                    isHighlighted={highlightedFieldIndex === index}
                    isExpanded={expandedFieldIndices.has(index)}
                    isTouched={touchedFieldIndices.has(index)}
                    availableFields={availableFields}
                    availableFilterFields={availableFilterFields}
                    calculationTypes={getCalculationTypesForField(field.field)}
                    innerRef={(el) => setFieldItemRef(index, el)}
                    onRemove={handleRemoveField}
                    onFieldSelect={handleFieldSelect}
                    onDisplayNameChange={handleDisplayNameChange}
                    onCalculationTypeChange={handleCalculationTypeChange}
                    onPrecisionChange={handlePrecisionChange}
                    onNormalizeChange={handleNormalizeChange}
                    onStatTestingChange={handleStatTestingChange}
                    onIsL1CategoryChange={handleIsL1CategoryChange}
                    onReorder={handleReorderFields}
                    onAccordionToggle={handleAccordionToggle}
                    onBlur={handleFieldItemBlur}
                    onAdvancedSettings={onAdvancedSettings}
                    onEditFilter={(fieldIndex, filterId) => onEditFilter?.(fieldIndex, filterId)}
                    onFilterRemove={handleFilterRemove}
                    defaultActiveTab={expandedIndex === index ? defaultActiveTab : undefined}
                  />
                );
              })}
        </div>
      ) : isLoading ? (
        <div className="fields-list fields-manager-skeleton-list">{renderFieldSkeleton(0)}</div>
      ) : (
        <div className="empty-state">
          <p>No fields added yet.</p>
        </div>
      )}
    </div>
  );
};

export default FieldsManager;
